import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { trpc } from "~/lib/trpc";
import { cn, formatNumber } from "~/lib/utils";
import { useT } from "~/lib/i18n";

interface MarketModel {
  kind: string;
  path: string;
  last_observed: string | null;
  n_active: number;
  n_deals: number;
}

export default function Marche(): React.JSX.Element {
  const { t, numberLocale } = useT();
  const [search, setSearch] = useState("");
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);

  const sitemapQ = trpc.seo.sitemap.useQuery({ kind: "model", limit: 500 });
  const models: MarketModel[] = (sitemapQ.data ?? []) as MarketModel[];

  const brands = useMemo(() => {
    const brandMap = new Map<string, { name: string; slug: string; count: number; deals: number }>();
    for (const m of models) {
      const segments = m.path.split("/").filter(Boolean);
      if (segments.length < 2) continue;
      const brandSlug = segments[0]!;
      const existing = brandMap.get(brandSlug);
      if (existing) {
        existing.count += m.n_active;
        existing.deals += m.n_deals;
      } else {
        brandMap.set(brandSlug, {
          name: brandSlug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
          slug: brandSlug,
          count: m.n_active,
          deals: m.n_deals,
        });
      }
    }
    return Array.from(brandMap.values()).sort((a, b) => b.count - a.count);
  }, [models]);

  const filteredModels = useMemo(() => {
    let result = models;
    if (selectedBrand) {
      result = result.filter((m) => m.path.startsWith(`/${selectedBrand}/`));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((m) => m.path.toLowerCase().includes(q));
    }
    return result;
  }, [models, selectedBrand, search]);

  const totalActive = useMemo(() => models.reduce((sum, m) => sum + m.n_active, 0), [models]);
  const totalDeals = useMemo(() => models.reduce((sum, m) => sum + m.n_deals, 0), [models]);

  React.useEffect(() => {
    document.title = `${t("web.market.title")} — MotorScan`;
    return () => { document.title = "MotorScan — The EU car scanner"; };
  }, [t]);

  return (
    <div className="w-full max-w-[1280px] mx-auto px-5 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <span className="mono text-[10.5px] uppercase tracking-[0.12em] text-ink-tertiary">
            {t("nav.market")}
          </span>
          <h1 className="mt-1 font-display text-[28px] sm:text-[34px] leading-[1.2] text-ink-primary">
            {t("web.market.title")}
          </h1>
          <p className="mt-2 text-[14px] text-ink-secondary max-w-[55ch]">
            {t("web.market.subtitle")}
          </p>
        </div>
        {/* Stats */}
        <div className="flex items-center gap-5">
          <StatPill label="Annonces" value={formatNumber(totalActive, numberLocale)} />
          <StatPill label="Deals" value={formatNumber(totalDeals, numberLocale)} accent />
          <StatPill label="Modèles" value={formatNumber(models.length, numberLocale)} />
        </div>
      </div>

      {/* Search bar */}
      <div className="relative mb-6">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un modèle..."
          className="w-full h-11 pl-10 pr-4 bg-surface-card border border-[var(--divider-strong)] rounded-lg text-[14px] text-ink-primary placeholder:text-ink-tertiary focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-shadow"
        />
        <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-tertiary" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
      </div>

      {/* Brand filter chips */}
      <div className="flex gap-2 flex-wrap mb-6">
        <button
          onClick={() => setSelectedBrand(null)}
          className={cn(
            "h-8 px-3 rounded-md text-[12px] font-medium border transition-colors",
            !selectedBrand
              ? "bg-[var(--accent)] text-[var(--ink-on-accent)] border-[var(--accent)]"
              : "bg-surface-card text-ink-secondary border-[var(--divider-strong)] hover:bg-surface-hover",
          )}
        >
          Tous ({models.length})
        </button>
        {brands.slice(0, 20).map((b) => (
          <button
            key={b.slug}
            onClick={() => setSelectedBrand(selectedBrand === b.slug ? null : b.slug)}
            className={cn(
              "h-8 px-3 rounded-md text-[12px] font-medium border transition-colors",
              selectedBrand === b.slug
                ? "bg-[var(--accent)] text-[var(--ink-on-accent)] border-[var(--accent)]"
                : "bg-surface-card text-ink-secondary border-[var(--divider-strong)] hover:bg-surface-hover",
            )}
          >
            {b.name} ({b.count})
          </button>
        ))}
      </div>

      {/* Loading */}
      {sitemapQ.isPending && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="h-24 rounded-lg border border-[var(--divider-weak)] bg-surface-sunken animate-pulse" />
          ))}
        </div>
      )}

      {/* Models grid */}
      {!sitemapQ.isPending && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredModels.map((m) => (
            <ModelCard key={m.path} model={m} numberLocale={numberLocale} />
          ))}
          {filteredModels.length === 0 && (
            <div className="col-span-full py-16 text-center text-ink-tertiary text-[13px]">
              {search ? "Aucun modèle trouvé pour cette recherche." : t("web.market.empty")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ModelCard({ model, numberLocale }: { model: MarketModel; numberLocale: string }): React.JSX.Element {
  const segments = model.path.split("/").filter(Boolean);
  const brandSlug = segments[0] ?? "";
  const modelSlug = segments[1] ?? "";
  const brandLabel = brandSlug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const modelLabel = modelSlug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Link
      to={`/marche/${brandSlug}/${modelSlug}`}
      className="group flex items-center justify-between gap-3 p-4 rounded-lg border border-[var(--divider-weak)] bg-surface-card hover:border-[var(--divider-strong)] hover:shadow-[0_2px_12px_-2px_rgba(0,0,0,0.1)] transition-all duration-200"
    >
      <div className="min-w-0">
        <div className="text-[14px] font-medium text-ink-primary group-hover:text-[var(--accent)] transition-colors truncate">
          {brandLabel} {modelLabel}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-[11.5px] text-ink-tertiary mono">
          <span>{formatNumber(model.n_active, numberLocale)} annonces</span>
          {model.n_deals > 0 && (
            <>
              <span className="opacity-40">·</span>
              <span className="text-deal-super font-medium">{model.n_deals} deals</span>
            </>
          )}
        </div>
      </div>
      <svg className="flex-shrink-0 w-4 h-4 text-ink-tertiary group-hover:text-[var(--accent)] transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </Link>
  );
}

function StatPill({ label, value, accent }: { label: string; value: string; accent?: boolean }): React.JSX.Element {
  return (
    <div className="text-center">
      <div className={cn("text-[20px] font-semibold mono leading-none", accent ? "text-deal-super" : "text-ink-primary")}>
        {value}
      </div>
      <div className="mt-1 text-[10.5px] uppercase tracking-[0.08em] text-ink-tertiary">{label}</div>
    </div>
  );
}
